'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { TrendingUp, Mail, AlertCircle, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { forgotUsername } from '@/lib/api-client';

export default function ForgotUsernamePage() {
  const { siteName, logoUrl } = useSite();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await forgotUsername(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-surface">
      <div className="w-full max-w-md">
        <div className="bg-card-bg border border-default rounded-2xl p-8">
          <div className="text-center mb-8">
            <Link href="/" className="inline-flex items-center gap-2 mb-4">
              {logoUrl ? (
                <img src={logoUrl} alt={siteName} className="h-9 w-auto object-contain" />
              ) : (
                <>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center">
                    <TrendingUp size={20} className="text-white" />
                  </div>
                  <span className="font-bold text-xl bg-gradient-to-r from-emerald-400 to-sky-400 bg-clip-text text-transparent">
                    {siteName}
                  </span>
                </>
              )}
            </Link>
            <h1 className="text-xl font-bold text-white">Forgot your username?</h1>
            <p className="text-fg-subtle text-sm mt-1">
              Enter your email and we&apos;ll send you your username.
            </p>
          </div>

          {sent ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white mb-1">Check your email</h2>
                  <p className="text-sm text-fg-muted">
                    If an account exists for <span className="text-white font-medium">{email}</span>, we&apos;ve sent your username to it.
                  </p>
                </div>
              </div>
              <Link href="/login"
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-default hover:border-muted text-slate-300 hover:text-white text-sm rounded-xl transition-colors">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                  <AlertCircle size={16} className="flex-shrink-0" /> {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Email address</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" autoComplete="email" autoFocus
                    className="w-full bg-surface border border-default focus:border-sky-500 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 text-sm transition-all" />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-500/50 text-white font-semibold py-3 rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : 'Send My Username'}
              </button>
              <Link href="/login"
                className="flex items-center justify-center gap-2 w-full py-2.5 text-fg-muted hover:text-white text-sm transition-colors">
                <ArrowLeft size={14} /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
